import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type OnChangeFn, type Row, type RowSelectionState, type SortingState, type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Columns3 } from 'lucide-react';
import {
  forwardRef, useRef, useState, type HTMLAttributes, type KeyboardEvent, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { Checkbox } from './choice';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from './overlay';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';

// ───────────────────────── Table primitives ─────────────────────────
export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => (
  <table ref={ref} className={cn('w-full caption-bottom border-separate border-spacing-0 text-sm', className)} {...props} />
));
Table.displayName = 'Table';
export const THead = (p: HTMLAttributes<HTMLTableSectionElement>) => <thead {...p} />;
export const TBody = (p: HTMLAttributes<HTMLTableSectionElement>) => <tbody {...p} />;
export const TR = ({ className, ...p }: HTMLAttributes<HTMLTableRowElement>) => <tr className={cn('group', className)} {...p} />;
export const TH = ({ className, ...p }: ThHTMLAttributes<HTMLTableCellElement>) => (
  <th
    className={cn(
      'sticky top-0 z-10 h-10 border-b border-border bg-surface-raised px-3 text-left text-xs font-medium text-text-muted first:pl-4 last:pr-4',
      className,
    )}
    {...p}
  />
);
export const TD = ({ className, ...p }: TdHTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('h-12 border-b border-border px-3 align-middle text-text first:pl-4 last:pr-4', className)} {...p} />
);

// ───────────────────────── DataTable ─────────────────────────
export interface DataTableProps<T> {
  readonly data: readonly T[];
  readonly columns: ColumnDef<T, unknown>[];
  /** Stable row id (defaults to `row.id` when present). */
  readonly getRowId?: (row: T, index: number) => string;
  /** Accessible table name. */
  readonly label: string;
  /** Enables a checkbox column; controlled selection optional. */
  readonly selectable?: boolean;
  readonly rowSelection?: RowSelectionState;
  readonly onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Enter / click on a row. */
  readonly onRowActivate?: (row: T) => void;
  readonly initialSorting?: SortingState;
  readonly initialVisibility?: VisibilityState;
  /** Show the column-visibility menu. */
  readonly columnToggle?: boolean;
  readonly empty?: ReactNode;
  readonly toolbar?: ReactNode;
  readonly className?: string;
  /** Max height of the scroll area (sticky header scrolls within it). */
  readonly maxHeight?: string;
}

const defaultRowId = <T,>(row: T, i: number): string =>
  row && typeof row === 'object' && 'id' in row && typeof row.id === 'string' ? row.id : String(i);

/**
 * TanStack Table with sorting, column visibility, sticky header, optional row selection and keyboard row
 * navigation (↑/↓ or j/k move, Enter activates, Space toggles selection, Home/End jump).
 * Column `meta.align = 'right'` right-aligns numeric columns.
 */
export const DataTable = <T,>({
  data, columns, getRowId = defaultRowId, label, selectable, rowSelection: rowSelectionProp, onRowSelectionChange,
  onRowActivate, initialSorting = [], initialVisibility = {}, columnToggle, empty, toolbar, className, maxHeight = '70vh',
}: DataTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [visibility, setVisibility] = useState<VisibilityState>(initialVisibility);
  const [selectionState, setSelectionState] = useState<RowSelectionState>({});
  const rowSelection = rowSelectionProp ?? selectionState;
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  const selectColumn: ColumnDef<T, unknown> = {
    id: '__select',
    enableSorting: false,
    enableHiding: false,
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows"
        checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false}
        onCheckedChange={(v) => table.toggleAllRowsSelected(v === true)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox aria-label="Select row" checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(v === true)} tabIndex={-1} />
    ),
  };

  const table = useReactTable<T>({
    data: data as T[],
    columns: selectable ? [selectColumn, ...columns] : columns,
    getRowId,
    state: { sorting, columnVisibility: visibility, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setVisibility,
    onRowSelectionChange: onRowSelectionChange ?? setSelectionState,
    enableRowSelection: Boolean(selectable),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const focusRow = (index: number) => {
    const rows = bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-row]');
    if (!rows?.length) return;
    rows[Math.max(0, Math.min(rows.length - 1, index))]?.focus();
  };

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, row: Row<T>, index: number) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'j':
        e.preventDefault();
        focusRow(index + 1);
        break;
      case 'ArrowUp':
      case 'k':
        e.preventDefault();
        focusRow(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(Number.MAX_SAFE_INTEGER);
        break;
      case 'Enter':
        if (onRowActivate) {
          e.preventDefault();
          onRowActivate(row.original);
        }
        break;
      case ' ':
        if (selectable) {
          e.preventDefault();
          row.toggleSelected();
        }
        break;
      default:
    }
  };

  const rows = table.getRowModel().rows;
  return (
    <div className={cn('rounded-lg border border-border bg-surface', className)}>
      {toolbar || columnToggle ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
          <div className="flex flex-wrap items-center gap-2">{toolbar}</div>
          {columnToggle ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" icon={<Columns3 aria-hidden />}>
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                {table
                  .getAllLeafColumns()
                  .filter((c) => c.getCanHide())
                  .map((c) => (
                    <DropdownPrimitive.CheckboxItem
                      key={c.id}
                      checked={c.getIsVisible()}
                      onCheckedChange={(v) => c.toggleVisibility(v)}
                      onSelect={(e) => e.preventDefault()}
                      className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm outline-none data-[highlighted]:bg-surface-sunken"
                    >
                      <span className={cn('size-4 rounded border border-border-strong', c.getIsVisible() && 'border-primary bg-primary')} aria-hidden />
                      {typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id}
                    </DropdownPrimitive.CheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-auto" style={{ maxHeight }}>
        <Table aria-label={label} aria-rowcount={rows.length}>
          <THead>
            {table.getHeaderGroups().map((hg) => (
              <TR key={hg.id}>
                {hg.headers.map((h) => {
                  const sorted = h.column.getIsSorted();
                  const right = (h.column.columnDef.meta as { align?: string } | undefined)?.align === 'right';
                  return (
                    <TH
                      key={h.id}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                      className={cn(right && 'text-right', h.column.id === '__select' && 'w-10')}
                    >
                      {h.isPlaceholder ? null : h.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          className={cn('inline-flex items-center gap-1 rounded hover:text-text', right && 'flex-row-reverse')}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {sorted === 'asc' ? <ArrowUp className="size-3.5" aria-hidden /> : sorted === 'desc' ? <ArrowDown className="size-3.5" aria-hidden /> : <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />}
                        </button>
                      ) : h.column.columnDef.header ? (
                        flexRender(h.column.columnDef.header, h.getContext())
                      ) : (
                        // unlabeled columns hold row actions; screen readers still get a header
                        <span className="sr-only">{h.column.id === '__select' ? 'Select' : 'Actions'}</span>
                      )}
                    </TH>
                  );
                })}
              </TR>
            ))}
          </THead>
          <tbody ref={bodyRef}>
            {rows.length === 0 ? null : (
              rows.map((row, i) => (
                <TR
                  key={row.id}
                  data-row
                  tabIndex={i === 0 ? 0 : -1}
                  aria-selected={selectable ? row.getIsSelected() : undefined}
                  onKeyDown={(e) => onRowKey(e, row, i)}
                  onClick={onRowActivate ? () => onRowActivate(row.original) : undefined}
                  onFocus={(e) => {
                    bodyRef.current?.querySelectorAll('tr[data-row]').forEach((r) => r.setAttribute('tabindex', '-1'));
                    e.currentTarget.setAttribute('tabindex', '0');
                  }}
                  className={cn(
                    'outline-none focus-visible:bg-primary-soft hover:bg-surface-sunken/60 data-[state=selected]:bg-primary-soft',
                    onRowActivate && 'cursor-pointer',
                  )}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TD
                      key={cell.id}
                      className={cn((cell.column.columnDef.meta as { align?: string } | undefined)?.align === 'right' && 'text-right tabular-nums')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TD>
                  ))}
                </TR>
              ))
            )}
          </tbody>
        </Table>
      </div>
      {/* outside the scroll area so it stays centred in the card on phones, however wide the columns are */}
      {rows.length === 0 ? (empty ?? <p className="py-10 text-center text-sm text-text-muted">Nothing to show yet.</p>) : null}
    </div>
  );
};

// ───────────────────────── Pagination ─────────────────────────
export interface PaginationProps {
  /** 1-based */
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
  readonly className?: string;
}

/** Previous / next pager with "Page x of y". For cursor lists, render a "Load more" Button instead. */
export const Pagination = ({ page, pageCount, onPageChange, className }: PaginationProps) => (
  <nav aria-label="Pagination" className={cn('flex items-center justify-end gap-2 text-sm text-text-muted', className)}>
    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} icon={<ChevronLeft aria-hidden />}>
      Previous
    </Button>
    <span className="tabular-nums" aria-current="page">
      Page {page} of {Math.max(1, pageCount)}
    </span>
    <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)} iconRight={<ChevronRight aria-hidden />}>
      Next
    </Button>
  </nav>
);
